/**
 * Normalizes every "what needs my attention" data source into a single
 * `ReviewItem[]` stream. Sole producer of review counts — the toolbar
 * gauges, Review-pane tab badges, per-row chips, and field-scoped
 * Inspector "Review items for this field" all read from this hook so
 * counts stay consistent.
 *
 * Inputs come from already-fetched payloads (mappings, mapping-health,
 * pending-decisions). No new endpoints. De-dup precedence ensures a
 * single underlying issue surfaced as both a generic warning and a
 * decision is shown only once (decision wins).
 */
import { useMemo } from 'react';
import type {
  FieldMapping,
  FieldStatus,
  HealthIssue,
  MappingResponse,
  TransformationPlanHint,
} from '../../services/types';
import type {
  CorrectionAttempt,
  DecisionKind,
  PendingDecision,
} from '../../services/mappingApi';
import { normalizeColumnsToArray } from '../graph/schemaResolution';
import { makeFieldKey } from '../state/useFieldSelection';

export type ReviewItemBucket =
  | 'error'
  | 'warning'
  | 'decision'
  | 'unmapped'
  | 'broken'
  | 'stale';

export type ReviewItemSeverity = 'error' | 'warning' | 'info';

export type ReviewPrimaryAction =
  | 'open_field'
  | 'resolve_decision'
  | 'review_drift'
  | 'open_table'
  | 'open_inline_edit'
  | 're_source';

export interface UnmappedTargetField {
  target_table: string;
  target_field: string;
  is_required?: boolean;
  hierarchy_path?: Record<string, string>;
}

export interface ReviewItem {
  id: string;
  bucket: ReviewItemBucket;
  severity: ReviewItemSeverity;
  scope: 'field' | 'table';
  target_table: string;
  target_field?: string;
  hierarchy_path?: Record<string, string>;
  message: string;
  source_field?: string;
  source_file?: string;
  operation?: string;
  confidence?: number;
  field_status?: FieldStatus;
  decision_kind?: DecisionKind;
  correction_history?: CorrectionAttempt[];
  deferred_count?: number;
  payload:
    | HealthIssue
    | PendingDecision
    | TransformationPlanHint
    | FieldMapping
    | UnmappedTargetField;
  primary_action?: ReviewPrimaryAction;
  /**
   * Plan §G/5: decision targets a field that no longer exists in the
   * schema snapshot. The Inspector renders an "Orphan target" subtitle
   * and `FieldDecisionActions` collapses to dismiss/defer only.
   */
  orphan_target?: boolean;
}

export interface UseReviewItemsInput {
  mappingResponse: MappingResponse | null | undefined;
  healthIssues: HealthIssue[] | undefined;
  pendingDecisions: PendingDecision[] | undefined;
  /**
   * Schema-derived unmapped target fields. The editor computes this
   * from the target schema snapshot minus the mapped set; passed in so
   * this hook stays independent of the canvas graph.
   */
  unmappedTargetFields: UnmappedTargetField[] | undefined;
}

export interface ReviewItemCounts {
  all: number;
  error: number;
  warning: number;
  decision: number;
  unmapped: number;
  broken: number;
  stale: number;
}

export interface UseReviewItemsResult {
  items: ReviewItem[];
  counts: ReviewItemCounts;
  /** Map keyed on `makeFieldKey(table, field)` → items addressing that field. */
  byField: Map<string, ReviewItem[]>;
  /** Map keyed on `T::table` → table-scoped items. */
  byTable: Map<string, ReviewItem[]>;
}

const LOW_CONFIDENCE_THRESHOLD = 0.6;

const HEALTH_BUCKET_FROM_SEVERITY: Record<ReviewItemSeverity, ReviewItemBucket> = {
  error: 'error',
  warning: 'warning',
  info: 'warning',
};

const BUCKET_PRECEDENCE: Record<ReviewItemBucket, number> = {
  decision: 5,
  error: 4,
  warning: 3,
  unmapped: 2,
  broken: 1,
  stale: 0,
};

function dedupKey(item: ReviewItem): string {
  // Stable key for de-dup — same target field + same conceptual issue.
  // Decision and warning describing the same rule violation will
  // collide here and the decision wins per BUCKET_PRECEDENCE.
  const fk = makeFieldKey(item.target_table, item.target_field, item.hierarchy_path);
  const ruleHint =
    (item.payload as HealthIssue).rule_type ??
    (item.payload as HealthIssue).category ??
    (item.payload as PendingDecision).code ??
    '';
  return `${fk}::${ruleHint}::${item.message}`;
}

export function useReviewItems(input: UseReviewItemsInput): UseReviewItemsResult {
  return useMemo(() => {
    const items: ReviewItem[] = [];
    const mappingResponse = input.mappingResponse;
    const mappings = mappingResponse?.mappings ?? [];

    // ---------------------------------------------------------------
    // 1. Broken / Stale (delta-generation drift)
    // ---------------------------------------------------------------
    for (const m of mappings) {
      if (m.field_status === 'broken' || m.field_status === 'stale') {
        const bucket: ReviewItemBucket = m.field_status === 'broken' ? 'broken' : 'stale';
        const severity: ReviewItemSeverity = m.field_status === 'broken' ? 'error' : 'warning';
        const id = `${bucket}:${makeFieldKey(m.target_table, m.target_field, m.hierarchy_path ?? undefined)}`;
        // Plan §G/6-7: when the break is specifically a missing source
        // file or column, surface "Re-source" as the primary action so
        // the operator jumps to inline edit instead of just "review".
        const reasonText = (m.break_reason ?? '').toLowerCase();
        const isSourceBroken =
          bucket === 'broken' &&
          (reasonText.includes('source') ||
            reasonText.includes('column') ||
            reasonText.includes('file'));
        items.push({
          id,
          bucket,
          severity,
          scope: 'field',
          target_table: m.target_table,
          target_field: m.target_field,
          hierarchy_path: m.hierarchy_path ?? undefined,
          message:
            m.break_reason ??
            (bucket === 'broken'
              ? 'Mapping inherited from parent recipe is broken in this batch.'
              : 'Mapping inherited from parent recipe needs review (schema drift).'),
          source_field: m.source_field,
          source_file: m.source_file,
          operation: m.operation ?? m.transformation_type,
          confidence: m.confidence,
          field_status: m.field_status,
          payload: m,
          primary_action: isSourceBroken ? 're_source' : 'review_drift',
        });
      }
    }

    // ---------------------------------------------------------------
    // 2. Low-confidence mappings → warnings
    // ---------------------------------------------------------------
    for (const m of mappings) {
      if (m.field_status === 'broken' || m.field_status === 'stale') continue;
      if (typeof m.confidence !== 'number') continue;
      if (m.confidence >= LOW_CONFIDENCE_THRESHOLD) continue;
      // Aggregation / deterministic ops with confidence 1 are skipped above.
      const id = `low_confidence:${makeFieldKey(m.target_table, m.target_field, m.hierarchy_path ?? undefined)}`;
      items.push({
        id,
        bucket: 'warning',
        severity: 'warning',
        scope: 'field',
        target_table: m.target_table,
        target_field: m.target_field,
        hierarchy_path: m.hierarchy_path ?? undefined,
        message: `Low confidence (${Math.round(m.confidence * 100)}%) — review the mapping.`,
        source_field: m.source_field,
        source_file: m.source_file,
        operation: m.operation ?? m.transformation_type,
        confidence: m.confidence,
        field_status: m.field_status,
        payload: m,
        primary_action: 'open_field',
      });
    }

    // ---------------------------------------------------------------
    // 3. Unmapped target fields (severity from is_required)
    // ---------------------------------------------------------------
    for (const u of input.unmappedTargetFields ?? []) {
      const id = `unmapped:${makeFieldKey(u.target_table, u.target_field, u.hierarchy_path)}`;
      items.push({
        id,
        bucket: 'unmapped',
        severity: u.is_required ? 'error' : 'warning',
        scope: 'field',
        target_table: u.target_table,
        target_field: u.target_field,
        hierarchy_path: u.hierarchy_path,
        message: u.is_required
          ? 'Required target column has no mapping.'
          : 'Optional target column has no mapping.',
        payload: u,
        primary_action: 'open_inline_edit',
      });
    }

    // ---------------------------------------------------------------
    // 4. Health issues (errors + warnings + hints from /health endpoint)
    // ---------------------------------------------------------------
    // Build a quick lookup of mappings by target field so health items
    // can carry the underlying mapping's confidence for filter chips.
    const mappingByField = new Map<string, FieldMapping>();
    for (const m of mappings) {
      mappingByField.set(makeFieldKey(m.target_table, m.target_field, m.hierarchy_path ?? undefined), m);
    }

    for (const h of input.healthIssues ?? []) {
      const bucket = HEALTH_BUCKET_FROM_SEVERITY[h.severity];
      const tf = h.target_field;
      const isFieldScope = Boolean(tf);
      const m = isFieldScope
        ? mappingByField.get(makeFieldKey(h.target_table, tf!))
        : undefined;
      const id = `health:${h.category}:${h.rule_type ?? ''}:${makeFieldKey(h.target_table, tf)}:${h.message}`;
      items.push({
        id,
        bucket,
        severity: h.severity,
        scope: isFieldScope ? 'field' : 'table',
        target_table: h.target_table,
        target_field: tf,
        hierarchy_path: m?.hierarchy_path ?? undefined,
        message: h.message,
        source_field: m?.source_field,
        source_file: m?.source_file,
        operation: m?.operation ?? m?.transformation_type,
        confidence: m?.confidence,
        field_status: m?.field_status,
        payload: h,
        primary_action: isFieldScope ? 'open_field' : 'open_table',
      });
    }

    // ---------------------------------------------------------------
    // 5. Transformation plan hints (validator / semantic / execution)
    // ---------------------------------------------------------------
    const planHints = mappingResponse?.transformation_plan?.hints ?? [];
    for (const hint of planHints) {
      const targetTable: string = (hint as any).target_table ?? '';
      const targetField: string | undefined = (hint as any).target_field;
      // Some hints address the whole plan; we surface those at table scope
      // when a table is identifiable, otherwise drop them — they're shown
      // by the global Health view when nothing is selected.
      if (!targetTable) continue;
      const isFieldScope = Boolean(targetField);
      const m = isFieldScope
        ? mappingByField.get(makeFieldKey(targetTable, targetField!))
        : undefined;
      const sev: ReviewItemSeverity = (hint.severity as ReviewItemSeverity) ?? 'warning';
      const bucket = HEALTH_BUCKET_FROM_SEVERITY[sev];
      const id = `hint:${hint.hint_id}`;
      items.push({
        id,
        bucket,
        severity: sev,
        scope: isFieldScope ? 'field' : 'table',
        target_table: targetTable,
        target_field: targetField,
        hierarchy_path: m?.hierarchy_path ?? undefined,
        message: hint.message,
        source_field: m?.source_field,
        source_file: m?.source_file,
        operation: m?.operation ?? m?.transformation_type,
        confidence: m?.confidence,
        field_status: m?.field_status,
        payload: hint,
        primary_action: isFieldScope ? 'open_field' : 'open_table',
      });
    }

    // Plan §G/5: detect orphan-target decisions — those whose target
    // field no longer appears in the schema snapshot. Build a set of
    // valid `(table, field)` pairs from schema columns so detection
    // doesn't depend on whether a mapping exists.
    const schemaTables: Record<string, { columns?: Array<{ name: string }> }> | undefined =
      (mappingResponse?.metadata as any)?.target_schema_snapshot?.tables ??
      (mappingResponse?.metadata as any)?.schema_context?.target_schema_snapshot?.tables ??
      (mappingResponse?.transformation_plan as any)?.target_schema_snapshot?.tables;
    const schemaFieldSet = new Set<string>();
    if (schemaTables && typeof schemaTables === 'object') {
      for (const [t, def] of Object.entries(schemaTables)) {
        for (const col of normalizeColumnsToArray(def?.columns)) {
          if (col?.name) schemaFieldSet.add(`${t}.${col.name}`.toLowerCase());
        }
      }
    }

    // ---------------------------------------------------------------
    // 6. Pending decisions (highest precedence)
    // ---------------------------------------------------------------
    for (const d of input.pendingDecisions ?? []) {
      const m = mappingByField.get(makeFieldKey(d.target_table, d.target_field));
      const id = `decision:${d.code}:${makeFieldKey(d.target_table, d.target_field)}`;
      const isOrphan =
        schemaFieldSet.size > 0 &&
        !schemaFieldSet.has(`${d.target_table}.${d.target_field}`.toLowerCase());
      items.push({
        id,
        bucket: 'decision',
        severity: d.severity,
        scope: 'field',
        target_table: d.target_table,
        target_field: d.target_field,
        hierarchy_path: m?.hierarchy_path ?? undefined,
        message: isOrphan
          ? `Orphan target — ${d.target_field} no longer in schema. ${d.message ?? d.payload.description ?? ''}`.trim()
          : (d.message ?? d.payload.description ?? `${d.code} — ${d.target_field}`),
        source_field: m?.source_field ?? d.payload.source_field ?? undefined,
        source_file: m?.source_file,
        operation: m?.operation ?? d.payload.mapping_op,
        confidence: m?.confidence,
        field_status: m?.field_status,
        decision_kind: d.kind,
        correction_history: d.correction_history,
        deferred_count: d.deferred_count,
        payload: d,
        primary_action: 'resolve_decision',
        orphan_target: isOrphan || undefined,
      });
    }

    // ---------------------------------------------------------------
    // De-dup by `dedupKey`. When two items collide, the higher-
    // precedence bucket wins.
    // ---------------------------------------------------------------
    const seen = new Map<string, ReviewItem>();
    for (const it of items) {
      const k = dedupKey(it);
      const prior = seen.get(k);
      if (!prior) {
        seen.set(k, it);
        continue;
      }
      if (BUCKET_PRECEDENCE[it.bucket] > BUCKET_PRECEDENCE[prior.bucket]) {
        seen.set(k, it);
      }
    }
    const deduped = Array.from(seen.values());

    // ---------------------------------------------------------------
    // Indexes
    // ---------------------------------------------------------------
    const byField = new Map<string, ReviewItem[]>();
    const byTable = new Map<string, ReviewItem[]>();
    const counts: ReviewItemCounts = {
      all: deduped.length,
      error: 0,
      warning: 0,
      decision: 0,
      unmapped: 0,
      broken: 0,
      stale: 0,
    };

    for (const it of deduped) {
      counts[it.bucket] += 1;
      if (it.scope === 'field' && it.target_field) {
        const k = makeFieldKey(it.target_table, it.target_field, it.hierarchy_path);
        const arr = byField.get(k);
        if (arr) arr.push(it);
        else byField.set(k, [it]);
      } else {
        const k = makeFieldKey(it.target_table);
        const arr = byTable.get(k);
        if (arr) arr.push(it);
        else byTable.set(k, [it]);
      }
    }

    return { items: deduped, counts, byField, byTable };
  }, [
    input.mappingResponse,
    input.healthIssues,
    input.pendingDecisions,
    input.unmappedTargetFields,
  ]);
}
